import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BlogCard } from '@/components/blog/BlogCard'
import { BlogLayout } from '@/components/blog/BlogLayout'
import { SEO } from '@/components/seo/SEO'
import { getAllPosts, getAllTags, slugifyTag } from '@/lib/blog'
import { Link } from 'react-router-dom'

export default function BlogIndex() {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const posts = useMemo(() => getAllPosts(language), [language])
  const tags = useMemo(() => getAllTags(language), [language])
  const [activeTag, setActiveTag] = useState<string | null>(null)

  const filteredPosts = useMemo(() => {
    if (!activeTag) return posts
    return posts.filter((post) => post.tags.includes(activeTag))
  }, [posts, activeTag])

  return (
    <>
      <SEO
        title={t('blog.index.seoTitle')}
        description={t('blog.index.seoDescription')}
        canonical="/blog"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Equna Blog',
          url: 'https://equna.eu/blog',
          inLanguage: language.startsWith('en') ? 'en-US' : 'de-DE',
          publisher: {
            '@type': 'Organization',
            name: 'Equna',
            url: 'https://equna.eu',
          },
        }}
      />
      <BlogLayout>
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {t('blog.index.title')}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">
            {t('blog.index.subtitle')}
          </p>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8">
              <button
                onClick={() => setActiveTag(null)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  activeTag === null
                    ? 'bg-[#237CCA] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {t('blog.allPosts')}
              </button>
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    activeTag === tag
                      ? 'bg-[#237CCA] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  #{tag}
                </button>
              ))}
              {tags.length > 0 && (
                <div className="basis-full text-xs text-slate-400 mt-1">
                  {t('blog.tagDeepLinkHint')}{' '}
                  {tags.map((tag, i) => (
                    <span key={tag}>
                      <Link
                        to={`/blog/tag/${slugifyTag(tag)}`}
                        className="underline hover:text-slate-600"
                      >
                        #{tag}
                      </Link>
                      {i < tags.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {filteredPosts.length === 0 ? (
            <p className="text-slate-500 py-12 text-center">{t('blog.empty')}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </section>
      </BlogLayout>
    </>
  )
}
